/* THE AVIARY ‚Äî free shipping progress bar with flying scarlet macaw.
   Works with snippets/cart-parrot-bar.liquid. No dependencies. */
(() => {
  if (window.__aviaryParrotBar) return;
  window.__aviaryParrotBar = true;

  const FLY_MS = 900; // must match the CSS transition duration
  const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

  // Survives the drawer/cart re-renders (the elements are replaced, this isn't)
  const state = { pct: null, qualified: null, lastBurst: 0 };

  const CONFETTI_COLOURS = ['#e02735', '#2f66b3', '#f0b03c', '#2e5339', '#d4a25a', '#fcfcfc'];
  const FEATHER_COLOURS = [
    ['#e02735', '#b7202e'],
    ['#2f66b3', '#223f77'],
    ['#f0b03c', '#c98f3f'],
  ];

  function formatMoney(cents, currency, locale) {
    const whole = cents % 100 === 0; // "¬£50" rather than "¬£50.00"
    try {
      const opts = { style: 'currency', currency: currency || 'GBP' };
      if (whole) {
        opts.minimumFractionDigits = 0;
        opts.maximumFractionDigits = 0;
      }
      return new Intl.NumberFormat(locale || 'en', opts).format(cents / 100);
    } catch (e) {
      return '¬£' + (whole ? cents / 100 : (cents / 100).toFixed(2));
    }
  }

  function isVisible(el) {
    if (typeof el.checkVisibility === 'function') return el.checkVisibility();
    const rect = el.getBoundingClientRect();
    return rect.width > 0 && getComputedStyle(el).visibility !== 'hidden';
  }

  class CartParrotBar extends HTMLElement {
    connectedCallback() {
      requestAnimationFrame(() => this.update(parseInt(this.dataset.total, 10) || 0));
    }

    update(totalCents) {
      const rate = parseFloat(window.Shopify && window.Shopify.currency && window.Shopify.currency.rate) || 1;
      const threshold = Math.max(1, Math.round(parseInt(this.dataset.thresholdGbp, 10) * rate));
      const empty = totalCents <= 0;
      const remaining = Math.max(0, threshold - totalCents);
      const pct = empty ? 0 : Math.min(100, (totalCents / threshold) * 100);
      const qualified = !empty && remaining <= 0;

      const msgEl = this.querySelector('[data-parrot-msg]');
      if (msgEl) {
        if (qualified) {
          msgEl.textContent = this.dataset.msgDone;
        } else if (empty) {
          this.renderTemplate(msgEl, this.dataset.msgEmpty, threshold);
        } else {
          this.renderTemplate(msgEl, this.dataset.msgAway, remaining);
        }
      }
      this.classList.toggle('is-done', qualified);
      this.moveBird(pct);

      if (qualified && state.qualified === false && Date.now() - state.lastBurst > 1500) {
        state.lastBurst = Date.now();
        const wait = this._flew ? FLY_MS - 100 : 0;
        setTimeout(() => celebrate(), wait);
      }

      state.pct = pct;
      state.qualified = qualified;
    }

    renderTemplate(msgEl, template, amountCents) {
      const amount = formatMoney(amountCents, this.dataset.currency, this.dataset.locale);
      const parts = (template || '').split('[amount]');
      msgEl.textContent = '';
      msgEl.append(document.createTextNode(parts[0] || ''));
      const strong = document.createElement('strong');
      strong.textContent = amount;
      msgEl.append(strong, document.createTextNode(parts[1] || ''));
    }

    moveBird(pct) {
      const fill = this.querySelector('.parrot-bar__fill');
      const bird = this.querySelector('.parrot-bar__bird');
      const track = this.querySelector('.parrot-bar__track');
      if (!fill || !bird || !track) return;

      // While an entrance replay is pending, every repaint keeps the bar
      // parked at the start ‚Äî whatever triggered it. The replay timer is
      // the only thing that releases the hold and starts the flight.
      if (this._holdUntilReplay) pct = 0;

      // The bird is anchored by its BODY, not its box (the CSS translateX
      // matches ANCHOR). Constants are the artwork's measured visible
      // bounds: tail tip 7.6% in, body centre 76.2%, beak tip 96%.
      // At 0% the tail tip touches the bar's start; at 100% the beak tip
      // touches the bar's end; in between the fill ends under his body,
      // so there is never unfilled track showing behind him.
      const ANCHOR = 0.762;
      const TAIL = 0.076;
      const BEAK = 0.96;
      let birdPct = 15;
      if (track.offsetWidth > 0 && bird.offsetWidth > 0) {
        birdPct = (bird.offsetWidth / track.offsetWidth) * 100;
      }
      const min = (ANCHOR - TAIL) * birdPct;
      const max = 100 - (BEAK - ANCHOR) * birdPct;
      const clampBird = (p) => Math.min(max, Math.max(min, p));
      const fillFor = (p, birdAt) => (p <= 0 ? 0 : Math.min(100, Math.max(p, birdAt)));

      const prevRaw = state.pct;
      const target = clampBird(pct);
      const fillPct = fillFor(pct, target);
      const prevBird = prevRaw === null ? null : clampBird(prevRaw);
      const prevFill = prevRaw === null ? null : fillFor(prevRaw, prevBird);
      const fillMoves = prevFill !== null && Math.abs(prevFill - fillPct) > 0.2;
      const birdMoves = prevBird !== null && Math.abs(prevBird - target) > 0.2;
      // Never animate inside a closed drawer ‚Äî nobody can see it, and the
      // flight is saved for the entrance replay when the drawer opens.
      const drawer = this.closest('.drawer');
      const hiddenInDrawer = !!(drawer && !drawer.classList.contains('active'));
      const animate = !reducedMotion.matches && !hiddenInDrawer && (fillMoves || birdMoves);
      this._flew = animate;

      if (!animate) {
        const fw = fillPct + '%';
        const bl = target + '%';
        // If a glide toward these exact targets is already running, leave it
        // be ‚Äî repainting would snap it to the end.
        if (fill.style.width !== fw || bird.style.left !== bl) {
          this.classList.add('no-anim');
          fill.style.width = fw;
          bird.style.left = bl;
          void this.offsetWidth; // apply before re-enabling transitions
          this.classList.remove('no-anim');
        }
        return;
      }

      // Start from where things last were, then glide to the new spot
      this.classList.add('no-anim');
      fill.style.width = prevFill + '%';
      bird.style.left = prevBird + '%';
      void this.offsetWidth;
      this.classList.remove('no-anim');

      if (birdMoves) this.classList.add('is-flying'); // wings flap only while the bird itself moves
      fill.style.width = fillPct + '%';
      bird.style.left = target + '%';

      clearTimeout(this._flyTimer);
      this._flyTimer = setTimeout(() => this.classList.remove('is-flying'), FLY_MS + 80);
    }

    burstConfetti() {
      if (reducedMotion.matches) return;
      const bird = this.querySelector('.parrot-bar__bird');
      const track = this.querySelector('.parrot-bar__track');
      if (!bird || !track) return;
      const x = bird.offsetLeft;
      for (let i = 0; i < 20; i++) {
        const bit = document.createElement('span');
        bit.className = 'parrot-confetti';
        const angle = Math.random() * Math.PI * 2;
        const dist = 2.4 + Math.random() * 4.6;
        bit.style.left = x + 'px';
        bit.style.setProperty('--c', CONFETTI_COLOURS[i % CONFETTI_COLOURS.length]);
        bit.style.setProperty('--dx', (Math.cos(angle) * dist).toFixed(2) + 'rem');
        bit.style.setProperty('--dy', (Math.sin(angle) * dist - 2.6).toFixed(2) + 'rem');
        bit.style.setProperty('--r', Math.round(Math.random() * 520 - 260) + 'deg');
        bit.style.setProperty('--d', (Math.random() * 0.12).toFixed(2) + 's');
        if (Math.random() < 0.4) bit.style.borderRadius = '50%';
        track.appendChild(bit);
        setTimeout(() => bit.remove(), 1400);
      }
    }

    disconnectedCallback() {
      clearTimeout(this._flyTimer);
      clearTimeout(this._replayTimer);
    }
  }

  if (!customElements.get('cart-parrot-bar')) customElements.define('cart-parrot-bar', CartParrotBar);

  function celebrate() {
    let burstText = '';
    document.querySelectorAll('cart-parrot-bar').forEach((el) => {
      if (!burstText) burstText = el.dataset.msgBurst || '';
      if (isVisible(el)) el.burstConfetti();
    });
    featherStorm(burstText);
  }

  function featherStorm(message) {
    if (reducedMotion.matches) return;
    if (document.getElementById('AviaryFeatherStorm')) return;

    const overlay = document.createElement('div');
    overlay.id = 'AviaryFeatherStorm';
    overlay.setAttribute('aria-hidden', 'true');
    overlay.style.cssText =
      'position:fixed;inset:0;overflow:hidden;pointer-events:none;z-index:10000;';

    const style = document.createElement('style');
    style.textContent =
      '@keyframes aviary-feather-fall{' +
      '0%{transform:translate3d(0,-12vh,0) rotate(0deg);opacity:0}' +
      '8%{opacity:1}' +
      '86%{opacity:1}' +
      '100%{transform:translate3d(var(--sx,0),108vh,0) rotate(var(--spin,420deg));opacity:0}}' +
      /* opacity:0 + fill-mode "both" keep feathers hidden above the screen
         during their staggered delays ‚Äî they only ever appear falling */
      '#AviaryFeatherStorm .aviary-feather{position:absolute;top:0;left:0;opacity:0;transform:translate3d(0,-12vh,0);animation:aviary-feather-fall var(--t,2.6s) cubic-bezier(0.3,0.15,0.65,0.9) var(--d,0s) both;will-change:transform,opacity}' +
      /* "YOU'VE GOT FREE SHIPPING" banner: pops in with the feathers,
         gone by the time the last of them lands */
      '@keyframes aviary-burst-msg{' +
      '0%{opacity:0;transform:translate(-50%,-50%) scale(0.92)}' +
      '10%{opacity:1;transform:translate(-50%,-50%) scale(1)}' +
      '80%{opacity:1}' +
      '100%{opacity:0;transform:translate(-50%,-50%) scale(0.98)}}' +
      '#AviaryFeatherStorm .aviary-burst-msg{position:fixed;left:50%;top:40%;transform:translate(-50%,-50%);' +
      'background:#2e5339;color:#fcfcfc;font-family:var(--font-heading-family,Montserrat,sans-serif);' +
      'font-weight:700;font-size:clamp(1.7rem,4vw,2.4rem);letter-spacing:0.12em;text-align:center;' +
      'padding:1.6rem 2.6rem;max-width:86vw;box-shadow:0 0.6rem 2.4rem rgba(2,9,18,0.25);' +
      'animation:aviary-burst-msg 3.4s ease both}';
    overlay.appendChild(style);

    if (message) {
      const banner = document.createElement('div');
      banner.className = 'aviary-burst-msg';
      banner.textContent = message;
      overlay.appendChild(banner);
    }

    for (let i = 0; i < 16; i++) {
      const [main, shade] = FEATHER_COLOURS[i % FEATHER_COLOURS.length];
      const size = 16 + Math.random() * 16;
      const feather = document.createElement('div');
      feather.className = 'aviary-feather';
      feather.style.left = 2 + Math.random() * 94 + 'vw';
      feather.style.setProperty('--sx', Math.round(Math.random() * 160 - 80) + 'px');
      feather.style.setProperty('--spin', Math.round(Math.random() * 720 - 360) + 'deg');
      feather.style.setProperty('--t', (2.1 + Math.random() * 1.1).toFixed(2) + 's');
      feather.style.setProperty('--d', (Math.random() * 0.5).toFixed(2) + 's');
      feather.innerHTML =
        '<svg width="' + size + '" height="' + size * 2.4 + '" viewBox="0 0 20 48" fill="none">' +
        '<path d="M10 1 C16 10 19 24 15 36 C13 42 11 45 10 47 C9 45 7 42 5 36 C1 24 4 10 10 1 Z" fill="' + main + '"/>' +
        '<path d="M10 4 L10 44" stroke="' + shade + '" stroke-width="1.4" stroke-linecap="round"/>' +
        '<path d="M10 12 C13 14 15 17 16 20 M10 20 C13 22 15 25 16 28 M10 12 C7 14 5 17 4 20 M10 20 C7 22 5 25 4 28" stroke="' + shade + '" stroke-width="0.7"/>' +
        '</svg>';
      overlay.appendChild(feather);
    }

    document.body.appendChild(overlay);
    setTimeout(() => overlay.remove(), 4200);
  }

  // Also listen to the theme's cart events, so the bar stays right even if a
  // future theme update changes which parts of the page get re-rendered.
  function refreshFromCart() {
    const root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
    fetch(root + 'cart.js')
      .then((r) => r.json())
      .then((c) => {
        if (!c || typeof c.total_price !== 'number') return;
        // An emptied cart still updates the bars: they stay visible
        // with the "Spend ¬£50 and get free shipping!" message.
        const total = c.item_count === 0 ? 0 : c.total_price;
        document.querySelectorAll('cart-parrot-bar').forEach((el) => {
          el.dataset.total = total;
          el.update(total);
        });
      })
      .catch(() => {});
  }

  if (typeof window.subscribe === 'function' && window.PUB_SUB_EVENTS && window.PUB_SUB_EVENTS.cartUpdate) {
    window.subscribe(window.PUB_SUB_EVENTS.cartUpdate, () => {
      clearTimeout(window.__aviaryParrotFetch);
      window.__aviaryParrotFetch = setTimeout(refreshFromCart, 150);
    });
  }

  // Every time the drawer opens (auto-open after add-to-cart, or a tap on
  // the cart icon), the bar parks at the start and, half a beat later, the
  // macaw flies the whole journey to the current total so shoppers watch
  // the progress build.
  const REPLAY_DELAY = 450;

  function replayEntrance(drawerEl) {
    if (reducedMotion.matches) return;
    drawerEl.querySelectorAll('cart-parrot-bar').forEach((el) => {
      if (typeof el.update !== 'function') return;
      const total = parseInt(el.dataset.total, 10) || 0;
      if (total <= 0) return; // empty cart: the bird just waits at the start
      clearTimeout(el._replayTimer);
      el._holdUntilReplay = true;
      state.pct = 0;
      el.moveBird(0); // park at the start instantly, while the drawer slides in
      el._replayTimer = setTimeout(() => {
        el._holdUntilReplay = false;
        state.pct = 0; // fly the full journey from the very start
        el.update(parseInt(el.dataset.total, 10) || 0);
      }, REPLAY_DELAY);
    });
  }

  const drawerHost = document.querySelector('cart-drawer');
  if (drawerHost && typeof MutationObserver === 'function') {
    let wasActive = drawerHost.classList.contains('active');
    new MutationObserver(() => {
      const isActive = drawerHost.classList.contains('active');
      if (isActive && !wasActive) replayEntrance(drawerHost);
      wasActive = isActive;
    }).observe(drawerHost, { attributes: true, attributeFilter: ['class'] });
  }

  // Keep the bird/fill alignment exact if the window is resized or a phone
  // rotates (the bird-to-track ratio changes, positions repaint instantly)
  window.addEventListener('resize', () => {
    clearTimeout(window.__aviaryParrotResize);
    window.__aviaryParrotResize = setTimeout(() => {
      document.querySelectorAll('cart-parrot-bar').forEach((el) => {
        el.update(parseInt(el.dataset.total, 10) || 0);
      });
    }, 150);
  });
})();
